import { useContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery as useQueryParams, useSwal } from '@kiper/hooks';
import { useTranslation } from 'react-i18next';
import theme from '@kiper/theme';
import { uploadFacial } from '../../services/api/invite';
import { FacialRegistrationContext } from './context';

export const FACIAL_STEPS = {
  PREPARATION: 0,
  CAPTURE: 1,
  CONFIRMATION: 2,
  SUBMITTING: 3,
};

export const FacialRegistrationProvider = ({
  children,
  primaryColor,
  secondaryColor,
}) => {
  const history = useHistory();
  const { inviteId } = useParams();
  const querystring = useQueryParams();
  const guestHash = querystring?.get('q')?.replace(/ /g, '+') || '';
  const { toast } = useSwal();
  const [t] = useTranslation('personalized-invite');

  const [step, setStep] = useState(FACIAL_STEPS.PREPARATION);
  const [capturedImage, setCapturedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storePhoto, setStorePhoto] = useState(true);

  const nextStep = useCallback(() => {
    setStep(prev => prev + 1);
  }, []);

  const previousStep = useCallback(() => {
    setStep(prev => Math.max(0, prev - 1));
  }, []);

  const resetFlow = useCallback(() => {
    setStep(FACIAL_STEPS.PREPARATION);
    setCapturedImage(null);
    setError(null);
  }, []);

  const goToInvitePage = useCallback(() => {
    history.push(`/convite-personalizado?q=${guestHash}`);
  }, [history, guestHash]);

  const capturePhoto = useCallback(
    imageData => {
      setCapturedImage(imageData);
      nextStep();
    },
    [nextStep],
  );

  const confirmPhoto = useCallback(async () => {
    nextStep();
    setLoading(true);

    const data = {
      storePhoto,
      face: capturedImage,
      hash: guestHash,
    };

    try {
      await uploadFacial({ data });
      setLoading(false);
      goToInvitePage();
    } catch (uploadError) {
      const errorMessage =
        uploadError?.response?.data?.[0]?.message ||
        uploadError.message ||
        t('facial-upload-error');

      toast.fire({ title: errorMessage, icon: 'error' });
      setError(errorMessage);
      setLoading(false);
      setStep(FACIAL_STEPS.CONFIRMATION);
    }
  }, [
    nextStep,
    goToInvitePage,
    capturedImage,
    guestHash,
    storePhoto,
    toast,
    t,
  ]);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setStep(FACIAL_STEPS.CAPTURE);
  }, []);

  const values = useMemo(
    () => ({
      step,
      setStep,
      nextStep,
      previousStep,
      resetFlow,
      goToInvitePage,
      capturedImage,
      capturePhoto,
      confirmPhoto,
      retakePhoto,
      loading,
      setLoading,
      error,
      setError,
      inviteId,
      guestHash,
      storePhoto,
      setStorePhoto,
      primaryColor,
      secondaryColor,
    }),
    [
      step,
      nextStep,
      previousStep,
      resetFlow,
      goToInvitePage,
      capturedImage,
      capturePhoto,
      confirmPhoto,
      retakePhoto,
      loading,
      error,
      inviteId,
      guestHash,
      storePhoto,
      primaryColor,
      secondaryColor,
    ],
  );

  return (
    <FacialRegistrationContext.Provider value={values}>
      {children}
    </FacialRegistrationContext.Provider>
  );
};

FacialRegistrationProvider.propTypes = {
  children: PropTypes.node.isRequired,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
};

FacialRegistrationProvider.defaultProps = {
  primaryColor: theme.colors.porterSecondary,
  secondaryColor: theme.colors.porterPrimary,
};

export const useFacialRegistrationContext = () => {
  const context = useContext(FacialRegistrationContext);
  if (!context) {
    throw new Error(
      'useFacialRegistrationContext must be used within a FacialRegistrationProvider',
    );
  }
  return context;
};
